import { MutableRefObject, useCallback, useEffect, useRef } from 'react';
import { Vega } from 'react-vega';
import { VegaProps } from 'react-vega/lib/Vega';
import { useElementSize } from '@mantine/hooks';
import clsx from 'clsx';
import deepEqual from 'deep-equal';
import { Error as LogLevelError, Spec, View } from 'vega';

import { LoadingIndicator } from '../loading-indicator/LoadingIndicator';

import styles from './vegaChart.module.scss';

export type Data = VegaProps['data'];

export type VegaChartProps = {
  spec: Spec;
  className?: string;
  data?: Data;
  heightfactor: number;
  isFullscreen?: boolean;
  loading?: boolean;
  minHeight?: number;
  vegaViewRef?: MutableRefObject<View | null>;
  onReadyCallback?: (v: View) => void;
};

const calculateVegaChartHeight = (
  isFullscreen: boolean,
  heightfactor: number
) => (isFullscreen ? '90vh' : `${heightfactor * 48}rem`);

const handleVegaError = (err: Error) => {
  console.error('Vega onError', err);
  throw err;
};

export function VegaChart({
  spec,
  data,
  className,
  heightfactor,
  isFullscreen = false,
  loading = false,
  vegaViewRef,
  minHeight,
  onReadyCallback,
}: VegaChartProps) {
  const {
    ref: wrapperRef,
    height: wrapperHeight,
    width: wrapperWidth,
  } = useElementSize();

  useEffect(() => {
    globalThis.dispatchEvent(new CustomEvent('resizevegachart'));
  }, [wrapperHeight, wrapperWidth]);

  // Force a full Vega remount when the data reference changes (e.g. after a
  // filter change) as react-vega does a live data patch, not a full view recreation.
  // So react-vega's live changeset patch leaves stale chart
  // marks visible alongside new ones, so a fresh view is required.
  // Tracking via a ref mutation during render is safe here — the ref is only
  // read as a key and never triggers an extra render cycle.
  const dataKeyRef = useRef(0);
  const prevDataRef = useRef(data);
  if (!deepEqual(prevDataRef.current, data, { strict: true })) {
    prevDataRef.current = data;
    dataKeyRef.current += 1;
  }

  // react-vega recreates the view when onNewView identity changes (see VegaEmbed
  // componentDidUpdate). Keep handleNewView stable and read the latest callback
  // from a ref so consumers can update setup logic without destroying the view.
  // The render-time assignment is required: useRef only seeds the initial value,
  // and a useEffect would run too late if the view is created on the first mount.
  const onReadyCallbackRef = useRef(onReadyCallback);
  onReadyCallbackRef.current = onReadyCallback;

  // vegaEmbed resolves asynchronously and every mount registers its own
  // onNewView callback, so the view of a mount that has already been replaced
  // can arrive last. Adopting it would leave the ref — and with it every signal
  // update sent through the ref — pointing at a finalized view, which accepts
  // signals and runs without error while nothing on screen changes.
  const dataKey = dataKeyRef.current;

  const handleNewView = useCallback(
    (view: View) => {
      if (!vegaViewRef || dataKey !== dataKeyRef.current) {
        return;
      }
      vegaViewRef.current = view;
      onReadyCallbackRef.current?.(view);
    },
    [dataKey, vegaViewRef]
  );

  return (
    <div
      className={clsx(styles['root'], className)}
      data-testid="vega-chart_root"
      // scale the height factor in frontend
      // 100% -> should take most of the space, but it should be clear if there is more content
      // 50% -> two elements should take most of the space, but it should be clear if there is more content
      // scaling value may need future adjustments
      style={{
        height: calculateVegaChartHeight(isFullscreen, heightfactor),
        minHeight,
      }}
      ref={wrapperRef}
    >
      {loading && (
        <div className={styles['overlay']}>
          <LoadingIndicator data-testid="vega-chart_loading-indicator" />
        </div>
      )}
      <Vega
        key={dataKey}
        onNewView={handleNewView}
        onError={handleVegaError}
        spec={spec}
        data={data}
        className={styles['vega']}
        actions={false}
        renderer="svg"
        logLevel={LogLevelError}
      />
    </div>
  );
}
